import { P } from '@piccolohealth/util';
import React from 'react';
import { FaArrowLeft, FaSearch } from 'react-icons/fa';
import { ChakraV3 } from '../..';

interface Props extends ChakraV3.InputProps {
  currentPagesLength: number;
  isSearchable: boolean;
  popPage: () => void;
}

export const CommandInput = React.forwardRef<HTMLInputElement, Props>((props, ref) => {
  const { popPage, currentPagesLength, isSearchable, onKeyDown, ...rest } = props;

  const inputRef = React.useRef<HTMLInputElement>(null);
  const mergedRef = ChakraV3.mergeRefs(ref, inputRef);

  // Focus after the browser paints so the input is
  // mounted and visible in portals/dialogs
  // requestAnimationFrame defers the focus call to the next frame,
  // after the browser has laid out and painted the element,
  // guaranteeing it's visible and focusable.
  React.useEffect(() => {
    requestAnimationFrame(() => {
      inputRef.current?.focus();
    });
  }, []);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && e.currentTarget.value === '' && currentPagesLength > 1) {
      e.preventDefault();
      popPage();
    }
    onKeyDown?.(e);
  };

  const icon = P.run(() => {
    if (currentPagesLength > 1) {
      return (
        <ChakraV3.IconButton size='2xs' variant='plain' onClick={() => popPage()}>
          <FaArrowLeft />
        </ChakraV3.IconButton>
      );
    }

    if (isSearchable) {
      return (
        <ChakraV3.Icon pointerEvents='none' color='gray.400'>
          <FaSearch />
        </ChakraV3.Icon>
      );
    }

    return null;
  });

  return (
    <ChakraV3.InputGroup startElement={icon} startElementProps={{ pointerEvents: 'auto' }}>
      <ChakraV3.Input
        ref={mergedRef}
        size='sm'
        variant='outline'
        border='none'
        {...rest}
        onKeyDown={handleKeyDown}
        _disabled={{
          cursor: 'auto',
        }}
      />
    </ChakraV3.InputGroup>
  );
});
